/**
 * Bringing a chart in from Lucid.
 *
 * TWO WAYS IN, because only one of them can be relied on. Fetching by
 * link needs an API key on the server, which is a thing somebody has to
 * go and set; pasting the contents needs nothing and works today. An
 * import that only offered the first would be a button that says "not
 * configured" to everybody who has not read the deployment notes.
 *
 * The paste path is not a fallback anybody should be ashamed of: it is
 * how a document gets across when the key belongs to a different Lucid
 * account from the one the chart lives in, which on shared work happens
 * more often than not.
 *
 * It MERGES onto the canvas rather than replacing it, like every other
 * way of putting shapes here. An import that wiped an arranged chain
 * would be a mistake nobody could undo past one step.
 */
import { useState } from 'react';
import { Field, Modal, Notice } from '../canvasUi';
import { canvasApiFetch } from '../../lib/canvasApi';
import { fromLucid, newId } from '../../lib/canvas/model';
import { lucidDocumentId, lucidPageFrom } from '../../lib/canvas/lucidUrl';

/**
 * Fresh ids for everything imported, with the connectors remapped.
 *
 * Lucid's ids come across faithfully from `fromLucid` — its tests depend
 * on that — but they cannot be used AS ids here. Importing the same page
 * twice would produce two shapes with one id, and React's answer to a
 * duplicate key is to duplicate or omit the element, which on a chain of
 * title is a box that silently disappears. Worse, every connector
 * resolves its endpoints by shape id, so the second import's arrows
 * would attach to the first import's boxes.
 *
 * The original is kept on `sourceId` so a party can still be recognised
 * later as the one that came from that Lucid document.
 */
function reIdentify({ shapes, edges, nextZ }) {
  const map = new Map();
  const fresh = shapes.map((shape) => {
    const id = newId('s');
    map.set(shape.id, id);
    return { ...shape, id, sourceId: shape.sourceId || shape.id, sourceKind: shape.sourceKind || 'lucid' };
  });
  const end = (e) => (e && e.shapeId
    ? { ...e, shapeId: map.get(e.shapeId) || e.shapeId }
    : e);
  return {
    shapes: fresh,
    edges: edges.map((edge) => ({
      ...edge, id: newId('e'), from: end(edge.from), to: end(edge.to),
    })),
    nextZ,
  };
}

/** Shift an imported chart clear of whatever is already drawn. */
function clearOf(doc, imported) {
  if (!doc.shapes.length || !imported.shapes.length) return imported;
  const floor = Math.max(...doc.shapes.map((s) => s.y + s.h)) + 200;
  const top = Math.min(...imported.shapes.map((s) => s.y));
  const shift = floor - top;
  if (shift <= 0) return imported;
  return {
    ...imported,
    shapes: imported.shapes.map((s) => ({ ...s, y: s.y + shift })),
  };
}

export default function LucidImport({ doc, onImport, onClose }) {
  const [link, setLink] = useState('');
  const [pasted, setPasted] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [needsKey, setNeedsKey] = useState(false);

  const bring = (page) => {
    const imported = reIdentify(fromLucid(page));
    if (!imported.shapes.length) {
      setError('That page has nothing on it that can be drawn here.');
      return false;
    }
    onImport(clearOf(doc, imported), imported);
    return true;
  };

  const fromLink = async () => {
    if (!lucidDocumentId(link)) {
      setError('That does not look like a Lucid link. Copy it from the address bar with the chart open.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const out = await canvasApiFetch('/api/lucid-import', {
        method: 'POST',
        body: { document: link },
      });
      const page = lucidPageFrom(out.contents, out.pageId);
      if (!page) { setError('Lucid answered, but that document has no page this can read.'); return; }
      if (bring(page)) onClose();
    } catch (err) {
      // A missing key is not a failure to report as one — it is a task,
      // and the person reading it is usually the person who can do it.
      // Read off the message because canvasApiFetch throws a plain Error: the
      // route's own words reach here, which is why they are written for
      // a person rather than for a log.
      const said = err?.message || 'Lucid could not be reached.';
      setNeedsKey(/LUCID_API_KEY/.test(said));
      setError(said);
    } finally {
      setBusy(false);
    }
  };

  const fromPaste = () => {
    setError(null);
    const page = lucidPageFrom(pasted.trim());
    if (!page) {
      setError('That is not a Lucid page. Paste the whole response, including the "pages" it starts with.');
      return;
    }
    if (bring(page)) onClose();
  };

  return (
    <Modal
      title="Bring a chart in from Lucid"
      onClose={onClose}
      footer={<button type="button" onClick={onClose}>Close</button>}
    >
      {error && (
        <Notice kind={needsKey ? 'warn' : 'error'} onDismiss={() => setError(null)}>
          {error}
        </Notice>
      )}

      <p className="faint">
        Everything on the page comes across — parties, instruments, connectors, the
        colours and the notes — and lands below whatever is already drawn here.
        Nothing on this canvas is replaced.
      </p>

      <Field
        label="From a link"
        hint="Open the chart in Lucid and copy the address. Needs a Lucid API key on the server, and only reaches documents that key can see."
      >
        <input
          value={link}
          placeholder="https://lucid.app/lucidchart/…/edit"
          onChange={(e) => setLink(e.target.value)}
        />
      </Field>
      <button type="button" className="btn-primary" onClick={fromLink} disabled={busy || !link.trim()}>
        {busy ? 'Fetching…' : 'Fetch and draw'}
      </button>

      <div className="divider" />

      <Field
        label="Or paste the contents"
        hint="Works with no key at all, and reaches a document the server's key cannot — somebody else's Lucid account, for instance."
      >
        <textarea
          rows={6}
          value={pasted}
          placeholder='{"pages": [ … ]}'
          onChange={(e) => setPasted(e.target.value)}
        />
      </Field>
      <button type="button" onClick={fromPaste} disabled={!pasted.trim()}>
        Draw what I pasted
      </button>
    </Modal>
  );
}
